import React, { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Divider,
  MenuItem,
  Paper,
  Tab,
  Tabs,
  TextField,
  Typography,
} from '@mui/material';
import {
  AddCircleOutline,
  RemoveCircleOutline,
  TrendingDown,
  TrendingUp,
} from '@mui/icons-material';
import portfolioTradingAPI from '../../services/portfolioTradingAPI';

const cardSx = {
  borderRadius: 4,
  border: '1px solid #dbe7f3',
  bgcolor: '#fff',
  boxShadow: '0 18px 50px rgba(15, 23, 42, 0.06)',
};

const formFieldSx = {
  '& .MuiOutlinedInput-root': {
    borderRadius: 2.6,
    bgcolor: '#ffffff',
    transition: 'all 160ms ease',
    '& fieldset': {
      borderColor: '#d6dee8',
      borderWidth: 1.5,
    },
    '&:hover fieldset': {
      borderColor: '#a9bdd4',
    },
    '&.Mui-focused': {
      boxShadow: '0 0 0 4px rgba(37, 99, 235, 0.08)',
    },
    '&.Mui-focused fieldset': {
      borderColor: '#2563eb',
    },
  },
  '& .MuiInputLabel-root': {
    color: '#64748b',
  },
  '& .MuiInputLabel-root.Mui-focused': {
    color: '#2563eb',
  },
  '& .MuiFormHelperText-root': {
    mt: 0.7,
    mx: 0,
    lineHeight: 1.35,
    fontSize: '0.74rem',
  },
};

const chipRowSx = {
  display: 'flex',
  gap: 0.75,
  flexWrap: 'wrap',
  alignItems: 'center',
};

const formGroupSx = {
  display: 'grid',
  gap: 0.55,
};

const popularTickers = ['HPG', 'FPT', 'MWG', 'TCB', 'MBB', 'ACB'];
const buyWeightPresets = [5, 10, 15, 20];
const sellWeightPresets = [25, 50, 75, 100];
const ROUND_LOT_SIZE = 100;

function formatWeightValue(value) {
  if (!Number.isFinite(value) || value <= 0) {
    return '';
  }

  return Number(value.toFixed(2)).toString();
}

function SummaryMetric({ label, value, tone = '#1e293b' }) {
  return (
    <Box sx={{ minWidth: 0 }}>
      <Typography sx={{ fontSize: '0.66rem', fontWeight: 800, color: '#94a3b8', textTransform: 'uppercase', letterSpacing: '0.06em' }}>
        {label}
      </Typography>
      <Typography
        sx={{
          mt: 0.35,
          fontSize: { xs: '1rem', sm: '1.28rem' },
          fontWeight: 900,
          color: tone,
          fontFamily: 'monospace',
          letterSpacing: { xs: '-0.04em', sm: '-0.02em' },
          lineHeight: 1.12,
          whiteSpace: 'nowrap',
          overflow: 'hidden',
          textOverflow: 'clip',
        }}
      >
        {value}
      </Typography>
    </Box>
  );
}

function normalizePriceInput(value) {
  return value.replace(/[^\d]/g, '');
}

function formatPriceInput(value, locale) {
  if (!value) {
    return '';
  }

  return Number(value).toLocaleString(locale);
}

function roundDownToLot(quantity, lotSize = ROUND_LOT_SIZE) {
  if (!Number.isFinite(quantity) || quantity <= 0) {
    return 0;
  }

  return Math.floor(quantity / lotSize) * lotSize;
}

export default function ModelPortfolioActions({ portfolioId, portfolio, positions = [], onTradeSuccess }) {
  const { t, i18n } = useTranslation();
  const locale = i18n.language === 'vi' ? 'vi-VN' : 'en-US';
  const [tab, setTab] = useState(0);
  const [priceLoading, setPriceLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [message, setMessage] = useState(null);
  const [error, setError] = useState(null);
  const [buyForm, setBuyForm] = useState({ ticker: '', weightPct: '', price: '' });
  const [sellForm, setSellForm] = useState({ ticker: '', weightPct: '', price: '' });
  const [buySubmitAttempted, setBuySubmitAttempted] = useState(false);
  const [sellSubmitAttempted, setSellSubmitAttempted] = useState(false);
  const [buyTouched, setBuyTouched] = useState({});
  const [sellTouched, setSellTouched] = useState({});

  const navTotal = Number(portfolio?.nav_total || 0);
  const navCash = Number(portfolio?.nav_cash || 0);
  const feeRate = Number(portfolio?.fee_rate ?? 0.0015);
  const sellTaxRate = Number(portfolio?.sell_tax_rate ?? 0.001);

  const selectedSellPosition = useMemo(
    () => positions.find((item) => item.ticker === sellForm.ticker) || null,
    [positions, sellForm.ticker]
  );
  const selectedBuyPosition = useMemo(
    () => positions.find((item) => item.ticker === buyForm.ticker) || null,
    [positions, buyForm.ticker]
  );

  const formatCurrency = (value) => new Intl.NumberFormat(locale, {
    style: 'currency',
    currency: 'VND',
    maximumFractionDigits: 0,
  }).format(value || 0);

  const loadPrice = async (ticker, mode) => {
    if (!ticker || ticker.length < 3) {
      return;
    }

    try {
      setPriceLoading(true);
      const result = await portfolioTradingAPI.getCurrentPrice(ticker);
      const price = result?.price || result?.current_price || result?.close;
      if (!price) {
        return;
      }

      const normalizedPrice = normalizePriceInput(String(Math.round(Number(price))));

      if (mode === 'buy') {
        setBuyForm((prev) => ({ ...prev, price: normalizedPrice }));
      } else {
        setSellForm((prev) => ({ ...prev, price: normalizedPrice }));
      }
    } catch (err) {
      console.error('Failed to load current price:', err);
    } finally {
      setPriceLoading(false);
    }
  };

  useEffect(() => {
    if (selectedSellPosition) {
      setSellForm((prev) => ({
        ...prev,
        price: prev.price || normalizePriceInput(String(Math.round(Number(selectedSellPosition.latest_price || 0)))),
      }));

      loadPrice(selectedSellPosition.ticker, 'sell');
    }
  }, [selectedSellPosition]);

  useEffect(() => {
    if (selectedBuyPosition?.latest_price) {
      setBuyForm((prev) => ({
        ...prev,
        price: prev.price || normalizePriceInput(String(Math.round(Number(selectedBuyPosition.latest_price)))),
      }));
    }
  }, [selectedBuyPosition]);

  const buyWeight = Number(buyForm.weightPct || 0);
  const buyPrice = Number(buyForm.price || 0);
  const buyGrossTarget = (navTotal * buyWeight) / 100;
  const buyQtyRaw = buyPrice > 0 ? Math.floor(buyGrossTarget / buyPrice) : 0;
  const buyQty = roundDownToLot(buyQtyRaw);
  const buyGrossValue = buyQty * buyPrice;
  const buyFee = buyGrossValue * feeRate;
  const buyNetCost = buyGrossValue + buyFee;
  const maxAffordableQtyRaw = buyPrice > 0
    ? Math.floor(navCash / (buyPrice * (1 + feeRate)))
    : 0;
  const maxAffordableQty = roundDownToLot(maxAffordableQtyRaw);
  const maxAffordableGrossValue = maxAffordableQty * buyPrice;
  const maxAffordableWeightPct = navTotal > 0
    ? (maxAffordableGrossValue / navTotal) * 100
    : 0;
  const canUseMaxBuy = buyPrice > 0 && navCash > 0 && maxAffordableQty > 0;

  const sellWeight = Number(sellForm.weightPct || 0);
  const sellPrice = Number(sellForm.price || 0);
  const sellGrossTarget = (navTotal * sellWeight) / 100;
  const sellQtyRaw = sellPrice > 0 ? Math.floor(sellGrossTarget / sellPrice) : 0;
  const sellQty = selectedSellPosition ? sellQtyRaw : 0;
  const sellQtyRounded = roundDownToLot(sellQty);
  const sellGrossValue = sellQtyRounded * sellPrice;
  const sellFee = sellGrossValue * feeRate;
  const sellTax = sellGrossValue * sellTaxRate;
  const sellNetValue = sellGrossValue - sellFee - sellTax;
  const currentSellWeight = selectedSellPosition && navTotal > 0 ? ((selectedSellPosition.market_value || 0) / navTotal) * 100 : 0;
  const buyCurrentWeight = useMemo(() => {
    const existingPosition = positions.find((item) => item.ticker === buyForm.ticker);
    if (!existingPosition || navTotal <= 0) {
      return 0;
    }

    return ((existingPosition.market_value || 0) / navTotal) * 100;
  }, [positions, buyForm.ticker, navTotal]);
  const sellableWeight = selectedSellPosition && selectedSellPosition.qty_total > 0
    ? currentSellWeight * ((selectedSellPosition.qty_sellable || 0) / selectedSellPosition.qty_total)
    : 0;

  const buyValidation = useMemo(() => {
    const errors = {};

    if (!buyForm.ticker) {
      errors.ticker = t('paperTrading.modelActions.validation.tickerRequired');
    } else if (buyForm.ticker.length < 3) {
      errors.ticker = t('paperTrading.modelActions.validation.tickerInvalid');
    }

    if (!buyForm.weightPct) {
      errors.weightPct = t('paperTrading.modelActions.validation.weightRequired');
    } else if (buyWeight <= 0) {
      errors.weightPct = t('paperTrading.modelActions.validation.weightPositive');
    } else if (buyWeight > 100) {
      errors.weightPct = t('paperTrading.modelActions.validation.weightTooHigh');
    }

    if (!buyForm.price) {
      errors.price = t('paperTrading.modelActions.validation.priceRequired');
    } else if (buyPrice <= 0) {
      errors.price = t('paperTrading.modelActions.validation.pricePositive');
    }

    if (!errors.weightPct && !errors.price && buyQty <= 0) {
      errors.quantity = t('paperTrading.modelActions.validation.quantityTooSmall');
    }

    return {
      errors,
      isValid: Object.keys(errors).length === 0,
    };
  }, [buyForm.ticker, buyForm.weightPct, buyForm.price, buyWeight, buyPrice, buyQty, t]);

  const sellValidation = useMemo(() => {
    const errors = {};

    if (!sellForm.ticker) {
      errors.ticker = t('paperTrading.modelActions.validation.holdingRequired');
    }

    if (!sellForm.weightPct) {
      errors.weightPct = t('paperTrading.modelActions.validation.weightRequired');
    } else if (sellWeight <= 0) {
      errors.weightPct = t('paperTrading.modelActions.validation.weightPositive');
    } else if (selectedSellPosition && sellWeight > currentSellWeight) {
      errors.weightPct = t('paperTrading.modelActions.validation.sellWeightTooHigh', {
        weight: currentSellWeight.toFixed(1),
      });
    } else if (selectedSellPosition && sellWeight > sellableWeight) {
      errors.weightPct = t('paperTrading.modelActions.validation.sellableWeightExceeded', {
        weight: sellableWeight.toFixed(1),
      });
    }

    if (!sellForm.price) {
      errors.price = t('paperTrading.modelActions.validation.priceRequired');
    } else if (sellPrice <= 0) {
      errors.price = t('paperTrading.modelActions.validation.pricePositive');
    }

    if (selectedSellPosition && (selectedSellPosition.qty_sellable || 0) <= 0) {
      errors.ticker = t('paperTrading.modelActions.validation.settlementPending');
    }

    if (!errors.weightPct && !errors.price && selectedSellPosition && sellQtyRounded > (selectedSellPosition.qty_sellable || 0)) {
      errors.weightPct = t('paperTrading.modelActions.validation.sellQtyExceeded', {
        qty: (selectedSellPosition.qty_sellable || 0).toLocaleString(locale),
      });
    }

    if (!errors.weightPct && !errors.price && selectedSellPosition && sellQtyRounded <= 0) {
      errors.quantity = t('paperTrading.modelActions.validation.quantityTooSmall');
    }

    return {
      errors,
      isValid: Object.keys(errors).length === 0,
    };
  }, [sellForm.ticker, sellForm.weightPct, sellForm.price, sellWeight, currentSellWeight, sellableWeight, sellPrice, selectedSellPosition, sellQtyRounded, t, locale]);

  const shouldShowBuyError = (field) => Boolean((buyTouched[field] || buySubmitAttempted) && buyValidation.errors[field]);
  const shouldShowSellError = (field) => Boolean((sellTouched[field] || sellSubmitAttempted) && sellValidation.errors[field]);

  useEffect(() => {
    if (buyForm.ticker.length === 3 && !buyForm.price) {
      loadPrice(buyForm.ticker, 'buy');
    }
  }, [buyForm.ticker, buyForm.price]);

  const handleBuySubmit = async (event) => {
    event.preventDefault();
    setMessage(null);
    setError(null);
    setBuySubmitAttempted(true);

    if (!buyValidation.isValid) {
      setError(buyValidation.errors.quantity || buyValidation.errors.price || buyValidation.errors.weightPct || buyValidation.errors.ticker || t('paperTrading.modelActions.buyInvalid'));
      return;
    }

    try {
      setSubmitting(true);
      const note = `[target weight +${buyWeight.toFixed(1)}%]`;
      await portfolioTradingAPI.buyStock(portfolioId, {
        ticker: buyForm.ticker,
        qty: buyQty,
        price: buyPrice,
        note,
      });
      setMessage(t('paperTrading.modelActions.buySuccess', {
        ticker: buyForm.ticker,
        weight: buyWeight.toFixed(1),
      }));
      setBuyForm({ ticker: '', weightPct: '', price: '' });
      setBuyTouched({});
      setBuySubmitAttempted(false);
      onTradeSuccess?.();
    } catch (err) {
      setError(err.message || t('paperTrading.modelActions.buyFailed'));
    } finally {
      setSubmitting(false);
    }
  };

  const handleSellSubmit = async (event) => {
    event.preventDefault();
    setMessage(null);
    setError(null);
    setSellSubmitAttempted(true);

    if (!sellValidation.isValid) {
      setError(sellValidation.errors.quantity || sellValidation.errors.price || sellValidation.errors.weightPct || sellValidation.errors.ticker || t('paperTrading.modelActions.sellInvalid'));
      return;
    }

    try {
      setSubmitting(true);
      const note = `[target weight -${sellWeight.toFixed(1)}%]`;
      await portfolioTradingAPI.sellStock(portfolioId, {
        ticker: sellForm.ticker,
        qty: sellQtyRounded,
        price: sellPrice,
        note,
      });
      setMessage(t('paperTrading.modelActions.sellSuccess', {
        ticker: sellForm.ticker,
        weight: sellWeight.toFixed(1),
      }));
      setSellForm({ ticker: '', weightPct: '', price: '' });
      setSellTouched({});
      setSellSubmitAttempted(false);
      onTradeSuccess?.();
    } catch (err) {
      setError(err.message || t('paperTrading.modelActions.sellFailed'));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Paper
      elevation={0}
      sx={{
        ...cardSx,
        p: { xs: 2, sm: 2.4 },
        position: 'sticky',
        top: 16,
        background: 'linear-gradient(180deg, #ffffff 0%, #fbfdff 100%)',
      }}
    >
      <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1.1, mb: 2 }}>
        <Box sx={{ width: 42, height: 42, minWidth: 42, minHeight: 42, aspectRatio: '1 / 1', flexShrink: 0, borderRadius: '16px', background: 'linear-gradient(135deg, #0f766e 0%, #0ea5e9 100%)', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#fff', boxShadow: '0 10px 20px rgba(14, 165, 233, 0.16)' }}>
          {tab === 0 ? <TrendingUp sx={{ fontSize: 17 }} /> : <TrendingDown sx={{ fontSize: 17 }} />}
        </Box>
        <Box sx={{ minWidth: 0 }}>
          <Typography sx={{ fontSize: { xs: '1.1rem', sm: '1.28rem' }, fontWeight: 850, color: '#24324a', letterSpacing: '-0.03em', lineHeight: 1.08 }}>
            {t('paperTrading.modelActions.title')}
          </Typography>
          <Typography sx={{ mt: 0.38, fontSize: '0.8rem', color: '#8a9ab3', maxWidth: 460, lineHeight: 1.45 }}>
            {t('paperTrading.modelActions.subtitle')}
          </Typography>
        </Box>
      </Box>

      <Tabs
        value={tab}
        onChange={(event, value) => {
          setTab(value);
          setMessage(null);
          setError(null);
          setBuySubmitAttempted(false);
          setSellSubmitAttempted(false);
        }}
        variant="fullWidth"
        sx={{
          mb: 1.9,
          minHeight: 52,
          p: 0.6,
          borderRadius: 3,
          bgcolor: '#f2f6fb',
          '& .MuiTabs-indicator': {
            display: 'none',
          },
          '& .MuiTab-root': {
            minHeight: 40,
            textTransform: 'none',
            fontSize: '0.82rem',
            fontWeight: 800,
            color: '#64748b',
            borderRadius: 2.2,
            transition: 'all 160ms ease',
            minWidth: 0,
          },
          '& .Mui-selected': {
            bgcolor: '#ffffff',
            color: '#2563eb !important',
            boxShadow: '0 6px 18px rgba(15, 23, 42, 0.08)',
          },
        }}
      >
        <Tab label={t('paperTrading.modelActions.buyTab')} icon={<AddCircleOutline sx={{ fontSize: 16 }} />} iconPosition="start" />
        <Tab label={t('paperTrading.modelActions.sellTab')} icon={<RemoveCircleOutline sx={{ fontSize: 16 }} />} iconPosition="start" />
      </Tabs>

      {message ? (
        <Alert severity="success" sx={{ mb: 1.5, borderRadius: 2 }} onClose={() => setMessage(null)}>
          {message}
        </Alert>
      ) : null}

      {error ? (
        <Alert severity="error" sx={{ mb: 1.5, borderRadius: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      ) : null}

      {tab === 0 ? (
        <Box component="form" onSubmit={handleBuySubmit} sx={{ display: 'grid', gap: 2.1 }}>
          <Box sx={formGroupSx}>
            <TextField
              label={t('paperTrading.modelActions.ticker')}
              size="small"
              value={buyForm.ticker}
              onChange={(event) => {
                setBuyTouched((prev) => ({ ...prev, ticker: true }));
                setBuyForm((prev) => ({ ...prev, ticker: event.target.value.toUpperCase().slice(0, 3) }));
              }}
              onBlur={() => loadPrice(buyForm.ticker, 'buy')}
              error={shouldShowBuyError('ticker')}
              helperText={shouldShowBuyError('ticker') ? buyValidation.errors.ticker : (buyCurrentWeight > 0 ? t('paperTrading.modelActions.currentWeightWithTicker', { current: buyCurrentWeight.toFixed(1), target: (buyCurrentWeight + buyWeight).toFixed(1) }) : null)}
              sx={formFieldSx}
              required
            />
            <Box sx={chipRowSx}>
              {popularTickers.map((ticker) => (
                <Chip
                  key={ticker}
                  label={ticker}
                  size="small"
                  onClick={() => {
                    setBuyTouched((prev) => ({ ...prev, ticker: true }));
                    setBuyForm((prev) => ({ ...prev, ticker }));
                    loadPrice(ticker, 'buy');
                  }}
                  color={buyForm.ticker === ticker ? 'primary' : 'default'}
                  variant={'filled'}
                  sx={{
                    height: 34,
                    fontSize: '0.78rem',
                    fontWeight: 800,
                    cursor: 'pointer',
                    borderRadius: '999px',
                    bgcolor: buyForm.ticker === ticker ? '#e8f0ff' : '#f3f4f6',
                    color: buyForm.ticker === ticker ? '#1d4ed8' : '#1e293b',
                    '&:hover': { bgcolor: buyForm.ticker === ticker ? '#dbe7ff' : '#e8edf3' },
                  }}
                />
              ))}
            </Box>
          </Box>

          <Box sx={formGroupSx}>
            <TextField
              label={t('paperTrading.modelActions.weightToAdd')}
              size="small"
              type="number"
              value={buyForm.weightPct}
              onChange={(event) => {
                setBuyTouched((prev) => ({ ...prev, weightPct: true }));
                setBuyForm((prev) => ({ ...prev, weightPct: event.target.value }));
              }}
              inputProps={{ min: 0, step: 0.1 }}
              error={shouldShowBuyError('weightPct')}
              helperText={shouldShowBuyError('weightPct') ? buyValidation.errors.weightPct : null}
              sx={formFieldSx}
              required
            />
            <Box sx={chipRowSx}>
              {buyWeightPresets.map((value) => (
                <Chip
                  key={value}
                  label={`+${value}%`}
                  size="small"
                  onClick={() => {
                    setBuyTouched((prev) => ({ ...prev, weightPct: true }));
                    setBuyForm((prev) => ({ ...prev, weightPct: String(value) }));
                  }}
                  variant={Number(buyForm.weightPct) === value ? 'filled' : 'outlined'}
                  sx={{
                    height: 34,
                    px: 0.4,
                    fontSize: '0.78rem',
                    fontWeight: 800,
                    cursor: 'pointer',
                    borderRadius: '999px',
                    borderColor: Number(buyForm.weightPct) === value ? '#cfe0ff' : '#d6dee8',
                    bgcolor: Number(buyForm.weightPct) === value ? '#eef4ff' : '#ffffff',
                    color: Number(buyForm.weightPct) === value ? '#1d4ed8' : '#24324a',
                  }}
                />
              ))}
              <Chip
                label={t('paperTrading.modelActions.maxBuy')}
                size="small"
                onClick={() => {
                  if (!canUseMaxBuy) {
                    return;
                  }

                  setBuyTouched((prev) => ({ ...prev, weightPct: true }));
                  setBuyForm((prev) => ({
                    ...prev,
                    weightPct: formatWeightValue(maxAffordableWeightPct),
                  }));
                }}
                disabled={!canUseMaxBuy}
                variant={buyForm.weightPct === formatWeightValue(maxAffordableWeightPct) ? 'filled' : 'outlined'}
                sx={{
                  height: 34,
                  px: 0.4,
                  fontSize: '0.78rem',
                  fontWeight: 800,
                  cursor: canUseMaxBuy ? 'pointer' : 'not-allowed',
                  borderRadius: '999px',
                  borderColor: buyForm.weightPct === formatWeightValue(maxAffordableWeightPct) ? '#cfe0ff' : '#d6dee8',
                  bgcolor: buyForm.weightPct === formatWeightValue(maxAffordableWeightPct) ? '#eef4ff' : '#ffffff',
                  color: buyForm.weightPct === formatWeightValue(maxAffordableWeightPct) ? '#1d4ed8' : '#24324a',
                  '&.Mui-disabled': {
                    borderColor: '#cbd5e1',
                    bgcolor: '#f1f5f9',
                    color: '#64748b',
                    opacity: 1,
                  },
                }}
              />
            </Box>
          </Box>

          <Box sx={formGroupSx}>
            <TextField
              label={t('paperTrading.modelActions.executionPrice')}
              size="small"
              type="text"
              value={formatPriceInput(buyForm.price, locale)}
              onChange={(event) => {
                setBuyTouched((prev) => ({ ...prev, price: true }));
                setBuyForm((prev) => ({ ...prev, price: normalizePriceInput(event.target.value) }));
              }}
              inputProps={{ inputMode: 'numeric' }}
              error={shouldShowBuyError('price')}
              helperText={shouldShowBuyError('price') ? buyValidation.errors.price : t('paperTrading.modelActions.autoPriceHint')}
              sx={formFieldSx}
              required
              InputProps={{ endAdornment: priceLoading ? <CircularProgress size={14} /> : null }}
            />
          </Box>

          {buyValidation.errors.quantity && (buyForm.weightPct || buyForm.price) ? (
            <Alert severity="warning" sx={{ borderRadius: 2 }}>
              {buyValidation.errors.quantity}
            </Alert>
          ) : null}

          <Box sx={{ mt: 0.3, p: 1.6, borderRadius: 3, bgcolor: '#f8fbfd', border: '1px solid #e8eef5' }}>
            <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' }, gap: 1.6 }}>
              <SummaryMetric label={t('paperTrading.modelActions.targetCapital')} value={formatCurrency(buyGrossTarget)} tone="#047857" />
              <SummaryMetric label={t('paperTrading.modelActions.estimatedQty')} value={buyQty.toLocaleString(locale)} tone="#047857" />
              <SummaryMetric label={t('paperTrading.modelActions.fee')} value={formatCurrency(buyFee)} tone="#475569" />
              <SummaryMetric label={t('paperTrading.modelActions.netCost')} value={formatCurrency(buyNetCost)} tone="#0f766e" />
            </Box>
          </Box>

          <Button type="submit" variant="contained" disabled={submitting || !buyValidation.isValid} sx={{ mt: 0.25, borderRadius: 999, py: 1.28, textTransform: 'none', fontWeight: 800, fontSize: '1rem', background: 'linear-gradient(135deg, #10b981 0%, #059669 100%)', boxShadow: '0 14px 28px rgba(16, 185, 129, 0.26)', '&:hover': { background: 'linear-gradient(135deg, #0ea875 0%, #047857 100%)', boxShadow: '0 16px 30px rgba(16, 185, 129, 0.3)' }, '&.Mui-disabled': { color: 'rgba(15, 118, 110, 0.72)', background: 'linear-gradient(135deg, rgba(16, 185, 129, 0.6) 0%, rgba(5, 150, 105, 0.72) 100%)' } }} startIcon={submitting ? <CircularProgress size={16} color="inherit" /> : <TrendingUp sx={{ fontSize: 18 }} />}>
            {t('paperTrading.modelActions.executeBuy')}
          </Button>
        </Box>
      ) : (
        <Box component="form" onSubmit={handleSellSubmit} sx={{ display: 'grid', gap: 2.1 }}>
          <Box sx={formGroupSx}>
            <TextField
              select
              label={t('paperTrading.modelActions.holding')}
              size="small"
              value={sellForm.ticker}
              onChange={(event) => {
                setSellTouched((prev) => ({ ...prev, ticker: true }));
                setSellForm({ ticker: event.target.value, weightPct: '', price: '' });
              }}
              error={shouldShowSellError('ticker')}
              helperText={shouldShowSellError('ticker') ? sellValidation.errors.ticker : null}
              sx={formFieldSx}
              required
            >
              {positions.map((position) => (
                <MenuItem key={position.ticker} value={position.ticker}>
                  {position.ticker} {position.qty_sellable > 0 ? `· ${t('paperTrading.modelActions.sellableQtyShort', { qty: position.qty_sellable.toLocaleString(locale) })}` : `· ${t('paperTrading.modelActions.lockedShort')}`}
                </MenuItem>
              ))}
            </TextField>
          </Box>

          <Box sx={formGroupSx}>
            <TextField
              label={t('paperTrading.modelActions.weightToReduce')}
              size="small"
              type="number"
              value={sellForm.weightPct}
              onChange={(event) => {
                setSellTouched((prev) => ({ ...prev, weightPct: true }));
                setSellForm((prev) => ({ ...prev, weightPct: event.target.value }));
              }}
              inputProps={{ min: 0, max: currentSellWeight || undefined, step: 0.1 }}
              error={shouldShowSellError('weightPct')}
              helperText={shouldShowSellError('weightPct') ? sellValidation.errors.weightPct : (selectedSellPosition ? t('paperTrading.modelActions.sellableWeight', { current: currentSellWeight.toFixed(1), sellable: sellableWeight.toFixed(1) }) : null)}
              sx={formFieldSx}
              required
            />
            {selectedSellPosition ? (
              <Box sx={chipRowSx}>
                {sellWeightPresets.map((percent) => {
                  const presetWeight = Math.min(currentSellWeight * (percent / 100), sellableWeight);
                  const roundedPreset = Number(presetWeight.toFixed(1));
                  return (
                    <Chip
                      key={percent}
                      label={percent === 100 ? t('paperTrading.modelActions.maxSellable') : `-${percent}%`}
                      size="small"
                      onClick={() => {
                        setSellTouched((prev) => ({ ...prev, weightPct: true }));
                        setSellForm((prev) => ({ ...prev, weightPct: String(roundedPreset) }));
                      }}
                      variant={Number(sellForm.weightPct) === roundedPreset ? 'filled' : 'outlined'}
                      disabled={roundedPreset <= 0}
                      sx={{
                        height: 34,
                        px: 0.4,
                        fontSize: '0.78rem',
                        fontWeight: 800,
                        cursor: 'pointer',
                        borderRadius: '999px',
                        borderColor: Number(sellForm.weightPct) === roundedPreset ? '#ffd1d1' : '#d6dee8',
                        bgcolor: Number(sellForm.weightPct) === roundedPreset ? '#fff1f2' : '#ffffff',
                        color: Number(sellForm.weightPct) === roundedPreset ? '#dc2626' : '#24324a',
                      }}
                    />
                  );
                })}
              </Box>
            ) : null}
          </Box>

          <Box sx={formGroupSx}>
            <TextField
              label={t('paperTrading.modelActions.executionPrice')}
              size="small"
              type="text"
              value={formatPriceInput(sellForm.price, locale)}
              onChange={(event) => {
                setSellTouched((prev) => ({ ...prev, price: true }));
                setSellForm((prev) => ({ ...prev, price: normalizePriceInput(event.target.value) }));
              }}
              inputProps={{ inputMode: 'numeric' }}
              error={shouldShowSellError('price')}
              helperText={shouldShowSellError('price') ? sellValidation.errors.price : t('paperTrading.modelActions.autoPriceHint')}
              sx={formFieldSx}
              required
              InputProps={{ endAdornment: priceLoading ? <CircularProgress size={14} /> : null }}
            />
          </Box>

          {sellValidation.errors.quantity && selectedSellPosition ? (
            <Alert severity="warning" sx={{ borderRadius: 2 }}>
              {sellValidation.errors.quantity}
            </Alert>
          ) : null}

          {selectedSellPosition ? (
            <Box sx={{ mt: 0.3, p: 1.6, borderRadius: 3, bgcolor: '#fff8f8', border: '1px solid #ffe1e1' }}>
              <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' }, gap: 1.6 }}>
                <SummaryMetric label={t('paperTrading.modelActions.sellableQty')} value={(selectedSellPosition.qty_sellable || 0).toLocaleString(locale)} tone="#b91c1c" />
                <SummaryMetric label={t('paperTrading.modelActions.estimatedQty')} value={sellQtyRounded.toLocaleString(locale)} tone="#b91c1c" />
                <SummaryMetric label={t('paperTrading.modelActions.estimatedGross')} value={formatCurrency(sellGrossValue)} tone="#475569" />
                <SummaryMetric label={t('paperTrading.modelActions.netProceeds')} value={formatCurrency(sellNetValue)} tone="#b91c1c" />
              </Box>
              <Divider sx={{ my: 1.35, borderColor: '#ffe1e1' }} />
              <Typography sx={{ fontSize: '0.74rem', color: '#94a3b8', lineHeight: 1.5 }}>
                {t('paperTrading.modelActions.sellHint')}
              </Typography>
            </Box>
          ) : null}

          <Button type="submit" variant="contained" disabled={submitting || !selectedSellPosition || !sellValidation.isValid} sx={{ mt: 0.25, borderRadius: 999, py: 1.28, textTransform: 'none', fontWeight: 800, fontSize: '1rem', background: 'linear-gradient(135deg, #ef4444 0%, #dc2626 100%)', boxShadow: '0 14px 28px rgba(239, 68, 68, 0.2)', '&:hover': { background: 'linear-gradient(135deg, #e53935 0%, #b91c1c 100%)', boxShadow: '0 16px 30px rgba(239, 68, 68, 0.24)' }, '&.Mui-disabled': { color: 'rgba(127, 29, 29, 0.72)', background: 'linear-gradient(135deg, rgba(239, 68, 68, 0.62) 0%, rgba(220, 38, 38, 0.72) 100%)' } }} startIcon={submitting ? <CircularProgress size={16} color="inherit" /> : <TrendingDown sx={{ fontSize: 18 }} />}>
            {t('paperTrading.modelActions.executeSell')}
          </Button>
        </Box>
      )}
    </Paper>
  );
}
